import { useCallback, useRef } from 'react';
import {
  cloneTaskFromClipboard,
  remapDependencies,
  attachNestedSprintsFromClipboard,
  generateId,
} from '../../../FlowchartView/hooks/clipboardUtils';
import { detectCustomFieldConflicts, getExistingFieldNamesSet, getReservedFieldNames } from '../../../FlowchartView/hooks/customFieldConflictUtils';
import { CATEGORY_COLORS } from '../../constants/colors';
import { getMostDistinctColor } from '../../../FlowchartView/utils/statusUtils';
import { persistPastedNestedSprints } from '../../../FlowchartView/hooks/clipboardUtils/nestedSprintPersistence';
import { mergePastedTasksIntoSprint } from './pasteMergeUtils';
import {
  preparePastedNestedForUndo,
  trashPastedNestedRoots,
  restorePastedNestedSprints,
} from './pasteNestedUndo';
import { schedulePasteAutoLayout } from './schedulePasteAutoLayout';

/**
 * usePasteWithCampaign Hook
 * 
 * Handles pasting tasks in multi-campaign mode.
 * Called after user selects a target campaign from the picker.
 * 
 * Manages:
 * - Cloning tasks with new IDs
 * - Position calculations (canvas vs intra-campaign coordinates)
 * - Tag and custom field merging (with conflict detection)
 * - Nested sprint cloning
 */
export const usePasteWithCampaign = ({
  setLocalSprint,
  localSprint,
  onAddSprint,
  onUpdateSprint,
  onTrashSprint,
  flowchartRef,
  recordAction,
  onCustomFieldConflict,
}) => {
  const pasteInFlightRef = useRef(false);

  const handlePasteWithCampaign = useCallback(async ({ 
    pasteData, 
    targetPosition: _targetPosition, 
    sourceCampaign, 
    campaignOffset: _campaignOffset,
    allTasks: _allTasks,
  }) => {
    if (pasteInFlightRef.current) {
      console.warn('[PASTE] Ignoring overlapping multi-campaign paste');
      return;
    }
    pasteInFlightRef.current = true;
    try {
      await runPasteWithCampaign();
    } finally {
      pasteInFlightRef.current = false;
    }

    async function runPasteWithCampaign() {
    const { 
      tasks: clipboardTasks, 
      nestedSprints: clipboardNestedSprints, 
      tagDefinitions = [], 
      customFieldDefinitions = [] 
    } = pasteData;

    // Capture pre-paste state for undo
    const prevTags = localSprint?.tags || localSprint?.categories || [];
    const prevCustomFields = localSprint?.customFields || [];
    
    const timestamp = Date.now();
    const idMap = new Map(); // originalId -> newId
    const optionIdMap = new Map(); // "originalTaskId-optionIdx" -> newOptionId
    
    // Clone all tasks with new IDs (same as regular paste)
    const clonedTasks = clipboardTasks.map((task, idx) => 
      cloneTaskFromClipboard(task, idMap, optionIdMap, timestamp, idx)
    );
    
    // Remap dependencies to new IDs
    remapDependencies(clonedTasks, idMap, optionIdMap);
    
    // ═══════════════════════════════════════════════════════════════
    // CUSTOM FIELD CONFLICT DETECTION
    // ═══════════════════════════════════════════════════════════════
    
    const { reusable, conflicts, reservedNameConflicts, newFields } = detectCustomFieldConflicts(
      customFieldDefinitions,
      prevCustomFields
    );
    
    // Build final custom field definitions with conflict resolution
    const fieldIdRemapping = new Map();
    const fieldsToAdd = [];
    
    // 1. Reusable fields: map to existing field ID
    for (const [clipboardIdx, existingId] of reusable.entries()) {
      const clipboardField = customFieldDefinitions[clipboardIdx];
      if (clipboardField?.id) {
        fieldIdRemapping.set(clipboardField.id, existingId);
      }
    }
    
    // 2. Handle conflicts: prompt user or auto-rename
    const existingFieldNames = getExistingFieldNamesSet(prevCustomFields);
    const reservedNames = getReservedFieldNames();
    const allUsedNames = new Set([...existingFieldNames, ...reservedNames]);
    
    for (const conflict of conflicts) {
      const { clipboardField, existingField } = conflict;
      let newName = null;
      
      if (onCustomFieldConflict) {
        try {
          newName = await onCustomFieldConflict({
            clipboardField,
            existingField,
            existingFieldNames: allUsedNames,
          });
        } catch (e) {
          console.log(`📋 Skipped conflicting field "${clipboardField.name}" (user cancelled)`);
          continue;
        }
      } else {
        // Auto-rename
        newName = `${clipboardField.name} (${clipboardField.type})`;
        let suffix = 1;
        while (allUsedNames.has(newName.toLowerCase())) {
          suffix++;
          newName = `${clipboardField.name} (${clipboardField.type} ${suffix})`;
        }
        console.log(`📋 Auto-renamed conflicting field "${clipboardField.name}" to "${newName}"`);
      }
      
      if (newName) {
        const newFieldId = generateId('cf', timestamp, fieldsToAdd.length);
        let newOptions;
        if (clipboardField.type === 'select' && clipboardField.options) {
          newOptions = clipboardField.options.map((opt, idx) => ({
            ...opt,
            id: generateId('opt', timestamp, fieldsToAdd.length, idx),
          }));
        }
        
        fieldsToAdd.push({
          ...clipboardField,
          id: newFieldId,
          name: newName,
          options: newOptions,
        });
        
        fieldIdRemapping.set(clipboardField.id, newFieldId);
        allUsedNames.add(newName.toLowerCase());
      }
    }
    
    // 2b. Handle reserved name conflicts: fields using built-in field names
    for (const conflict of reservedNameConflicts) {
      const { clipboardField, reservedName } = conflict;
      let newName = null;
      
      if (onCustomFieldConflict) {
        try {
          newName = await onCustomFieldConflict({
            clipboardField,
            existingField: { name: reservedName, type: 'built-in' },
            existingFieldNames: allUsedNames,
          });
        } catch (e) {
          console.log(`📋 Skipped reserved name field "${clipboardField.name}" (user cancelled)`);
          continue;
        }
      } else {
        // Auto-rename
        newName = `${clipboardField.name} (custom)`;
        let suffix = 1;
        while (allUsedNames.has(newName.toLowerCase())) {
          suffix++;
          newName = `${clipboardField.name} (custom ${suffix})`;
        }
        console.log(`📋 Auto-renamed reserved name field "${clipboardField.name}" to "${newName}"`);
      }
      
      if (newName) {
        const newFieldId = generateId('cf', timestamp, fieldsToAdd.length);
        let newOptions;
        if (clipboardField.type === 'select' && clipboardField.options) {
          newOptions = clipboardField.options.map((opt, idx) => ({
            ...opt,
            id: generateId('opt', timestamp, fieldsToAdd.length, idx),
          }));
        }
        
        fieldsToAdd.push({
          ...clipboardField,
          id: newFieldId,
          name: newName,
          options: newOptions,
        });
        
        fieldIdRemapping.set(clipboardField.id, newFieldId);
        allUsedNames.add(newName.toLowerCase());
      }
    }
    
    // 3. New fields: add directly
    for (const { field: clipboardField } of newFields) {
      let fieldName = clipboardField.name;
      
      if (allUsedNames.has(fieldName.toLowerCase())) {
        let suffix = 1;
        let newName = `${fieldName} (${suffix})`;
        while (allUsedNames.has(newName.toLowerCase())) {
          suffix++;
          newName = `${fieldName} (${suffix})`;
        }
        fieldName = newName;
      }
      
      fieldsToAdd.push({
        ...clipboardField,
        name: fieldName,
      });
      
      fieldIdRemapping.set(clipboardField.id, clipboardField.id);
      allUsedNames.add(fieldName.toLowerCase());
    }
    
    // 4. Remap custom field values in cloned tasks
    const remappedClonedTasks = clonedTasks.map(task => {
      if (!task.customFieldValues || Object.keys(task.customFieldValues).length === 0) {
        return task;
      }
      
      const remappedValues = {};
      for (const [oldFieldId, value] of Object.entries(task.customFieldValues)) {
        const newFieldId = fieldIdRemapping.get(oldFieldId);
        if (newFieldId) {
          const oldField = customFieldDefinitions.find(f => f.id === oldFieldId);
          const newField = fieldsToAdd.find(f => f.id === newFieldId);
          
          if (oldField?.type === 'select' && newField?.options && typeof value === 'string') {
            const oldOptIdx = oldField.options?.findIndex(o => o.id === value);
            if (oldOptIdx !== undefined && oldOptIdx >= 0 && newField.options[oldOptIdx]) {
              remappedValues[newFieldId] = newField.options[oldOptIdx].id;
            }
          } else if (oldFieldId !== newFieldId) {
            remappedValues[newFieldId] = value;
          } else {
            remappedValues[newFieldId] = value;
          }
        }
      }
      
      return {
        ...task,
        customFieldValues: Object.keys(remappedValues).length > 0 ? remappedValues : undefined,
      };
    });
    
    // Attach nested campaigns BEFORE building the saved task payload.
    // attachNestedSprintsFromClipboard mutates tasks in-place (sets nestedSprintId).
    // If we map/spread first, those links are lost on the parent while nested
    // campaigns still get parentSprintId — sidebar shows them, flow has no cards.
    const clonedNestedSprints = attachNestedSprintsFromClipboard(
      remappedClonedTasks,
      clipboardNestedSprints,
      timestamp,
    );

    const tasksWithCampaign = remappedClonedTasks.map((task) => ({
      ...task,
      sourceCampaign,
    }));
    
    const updatedSprint = mergePastedTasksIntoSprint(localSprint, {
      pastedTasks: tasksWithCampaign,
      tagDefinitions,
      fieldsToAdd,
    });
    if (!updatedSprint) return;

    setLocalSprint(updatedSprint);

    await persistPastedNestedSprints({
      nestedSprints: clonedNestedSprints,
      rootParentId: localSprint?.id,
      onAddSprint,
      onUpdateSprint,
      parentSprint: updatedSprint,
    });
    
    console.log(`📋 Pasted ${tasksWithCampaign.length} task(s) into campaign "${sourceCampaign.name}"`);
    schedulePasteAutoLayout(flowchartRef);

    const pastedTaskIds = tasksWithCampaign.map((t) => t.id);
    const rootParentId = localSprint?.id;
    const { snapshots: nestedSnapshots, rootIds: nestedRootIds } = preparePastedNestedForUndo(
      clonedNestedSprints,
      rootParentId,
    );
    const finalFieldsToAdd = [...fieldsToAdd];
    const finalPastedTasks = [...tasksWithCampaign];

    if (recordAction) {
      const taskCount = tasksWithCampaign.length;
      const taskNames = tasksWithCampaign.slice(0, 3).map((t) => t.name).join(', ');
      const description = taskCount === 1
        ? `Paste: "${tasksWithCampaign[0].name}"`
        : `Paste ${taskCount} tasks: ${taskNames}${taskCount > 3 ? '...' : ''}`;

      recordAction({
        type: 'PASTE_TASKS',
        description,
        undo: () => {
          setLocalSprint((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: (prev.tasks || []).filter((t) => !pastedTaskIds.includes(t.id)),
              tags: prevTags,
              categories: prevTags,
              customFields: prevCustomFields,
            };
          });
          trashPastedNestedRoots(nestedRootIds, onTrashSprint);
        },
        redo: () => {
          setLocalSprint((prev) => {
            if (!prev) return prev;
            const existingTasks = prev.tasks || [];
            const tasksWithOrder = finalPastedTasks.map((task, idx) => ({
              ...task,
              order: existingTasks.length + idx + 1,
            }));

            let updatedTags = prev.tags || prev.categories || [];
            const existingTagNames = new Set(updatedTags.map((t) => t.name));
            for (const tagName of new Set(finalPastedTasks.flatMap((t) => t.tags || [t.category]).filter(Boolean))) {
              if (!existingTagNames.has(tagName)) {
                const clipboardTag = tagDefinitions.find((t) => t.name === tagName);
                const color = clipboardTag?.color || getMostDistinctColor(
                  updatedTags.map((t) => t.color).filter(Boolean),
                  CATEGORY_COLORS
                );
                updatedTags = [...updatedTags, { name: tagName, color }];
              }
            }

            let updatedCustomFields = prev.customFields || [];
            const existingFieldIds = new Set(updatedCustomFields.map((f) => f.id));
            for (const fieldToAdd of finalFieldsToAdd) {
              if (!existingFieldIds.has(fieldToAdd.id)) {
                updatedCustomFields = [...updatedCustomFields, {
                  ...fieldToAdd,
                  order: updatedCustomFields.length,
                }];
              }
            }

            return {
              ...prev,
              tags: updatedTags,
              categories: updatedTags,
              customFields: updatedCustomFields,
              tasks: [...existingTasks, ...tasksWithOrder],
            };
          });

          schedulePasteAutoLayout(flowchartRef);

          restorePastedNestedSprints(nestedSnapshots, rootParentId, {
            onUpdateSprint,
            onAddSprint,
          });
        },
      });
    }
    } // runPasteWithCampaign
  }, [setLocalSprint, localSprint, onAddSprint, onUpdateSprint, onTrashSprint, flowchartRef, recordAction, onCustomFieldConflict]);

  return { handlePasteWithCampaign };
};

export default usePasteWithCampaign;
